import { ApiError, isInternalErrorMessage, isNetworkError, isUnauthenticatedError } from '~/composables/useApi'
import {
  formatCvMissingFieldsMessage,
  getCvMissingFieldsForApply,
  isCvApplyMissingField,
  isCvReadyForApply,
} from '~/utils/cv-validation'

const FIELD_LABEL_KEYS: Record<string, string> = {
  'personal.full_name': 'cv.fullName',
  'personal.email': 'cv.email',
  'personal.whatsapp': 'cv.whatsapp',
  'personal.dob': 'cv.dob',
  'personal.website': 'cv.website',
  'personal.linkedin': 'cv.linkedin',
  'personal.github': 'cv.github',
  'personal.sex': 'cv.sex',
  'personal.address_details': 'cv.addressDetails',
  'personal.country_id': 'cv.country',
  'personal.state_id': 'cv.state',
  'personal.city_id': 'cv.city',
  'personal.city_text': 'cv.city',
  'logo_url': 'storeLanding.logoEdit.title',
  'website': 'company.website',
  'name': 'company.name',
  'title': 'publish.detailsTitle',
  'phone': 'auth.phone',
  'password': 'auth.password',
}

type TranslateFn = (key: string, params?: Record<string, string | number>) => string

function fieldLabel(field: string, t: TranslateFn): string {
  const key = FIELD_LABEL_KEYS[field]
  if (key) {
    const label = t(key)
    if (label !== key) return label
  }
  const tail = field.split('.').pop() ?? field
  return tail.replace(/_/g, ' ')
}

function formatValidationMessage(field: string, message: string, t: TranslateFn): string {
  const label = fieldLabel(field, t)
  const lower = message.toLowerCase()

  if (lower.includes('valid url') || lower.includes('must be a url')) {
    return t('error.invalidUrl', { field: label })
  }
  if (lower.includes('valid email') || lower.includes('must be a valid email')) {
    return t('error.invalidEmail', { field: label })
  }
  if (lower.includes('required')) {
    return t('error.required', { field: label })
  }
  if (lower.includes('must be at least')) {
    return t('error.minLength', { field: label })
  }
  if (lower.includes('must not be greater') || lower.includes('may not be greater')) {
    if (field === 'logo_url') {
      const logoTooLarge = t('storeLanding.logoEdit.tooLarge')
      if (logoTooLarge !== 'storeLanding.logoEdit.tooLarge') return logoTooLarge
    }
    return t('error.maxLength', { field: label })
  }

  return message
}

export function formatApiError(error: unknown, t: TranslateFn, lang: string = 'en'): string {
  if (isNetworkError(error)) {
    const translated = t('error.network')
    if (translated !== 'error.network') {
      return translated
    }
  }

  if (error instanceof ApiError) {
    if (isUnauthenticatedError(error)) {
      return ''
    }

    if (error.status === 429) {
      const translated = t('error.rateLimited')
      if (translated !== 'error.rateLimited') {
        return translated
      }
    }

    const entries = Object.entries(error.errors).filter(([, messages]) => messages?.length)
    if (entries.length) {
      return entries
        .flatMap(([field, messages]) =>
          messages.map(message => formatValidationMessage(field, message, t)),
        )
        .join('\n')
    }

    if (error.code === 'cvRequired') {
      const missing = error.missing.filter(isCvApplyMissingField)
      if (missing.length) {
        return formatCvMissingFieldsMessage(missing, t, lang)
      }
    }

    if (error.code) {
      const translated = t(`error.${error.code}`)
      if (translated !== `error.${error.code}`) {
        return translated
      }
    }

    if (error.status >= 500 || isInternalErrorMessage(error.message)) {
      return t('error.generic')
    }

    if (error.message && error.message !== 'Request failed') {
      return error.message
    }
  }

  if (error instanceof Error && error.message && error.message !== 'generic') {
    if (isNetworkError(error)) {
      return t('error.network')
    }
    if (isInternalErrorMessage(error.message)) {
      return t('error.generic')
    }
    return error.message
  }

  return t('error.generic')
}

export function useApiError() {
  const { t, lang } = useKawadernaI18n()
  const { show } = useToast()

  function showApiError(error: unknown, fallback?: string) {
    if (isUnauthenticatedError(error)) {
      useAuthModal().openAuth('signin')
      return
    }
    const message = formatApiError(error, t, lang.value)
    show(message || fallback || t('error.generic'))
  }

  return { formatApiError: (error: unknown) => formatApiError(error, t, lang.value), showApiError }
}
